/**
 * App-wide typed facility / well search (API # or lease name).
 *
 * Multi-word queries ("angel eyes") fetch extra rows, prefer wells for pad UIs,
 * and re-rank so phrase hits surface above partial single-token noise.
 *
 * Prefix / name search pages the catalog until exhausted — never a silent 20/100 cap.
 * The well is unknown until the user types it, so every match must be reachable.
 */

import { isNmOcdFacilityId } from '../../shared/facilityApiSearchIntent';
import {
  apiStateAbbrFromDigits,
  facilityNameMatchesQuery,
  looksLikeApiSearchQuery,
  nmCountyPrefixFromShorthandDigits,
  queryIsOutOfScopeApiState,
  rankFacilitySearchHits,
} from './facilitySearchMatch';

export type FacilitySearchHit = {
  id: string;
  name: string;
  apiNumber: string;
  latitude: number | null;
  longitude: number | null;
  county: string | null;
  state: string | null;
  operator: string | null;
  /** Raw type from API when present (well, facility, pipeline, …). */
  type?: string | null;
  status?: string | null;
  /** Original facility row for call sites that need extra fields. */
  raw?: Record<string, unknown>;
};

export type SearchFacilitiesByQueryOptions = {
  /**
   * Optional explicit cap. Omit to return every matching well/facility.
   * Tap API / well-name / speak must omit this.
   */
  limit?: number;
  /**
   * Pad / lease UIs: hit type=wells first so pipelines don't bury well names.
   * Map-wide search can set preferWells: false to keep type=all primary.
   */
  preferWells?: boolean;
  /** Optional fetch headers (auth for worklist / water). */
  headers?: HeadersInit;
  /** When set, skip results that score below this (0 = keep all ranked). */
  minScore?: number;
  /** Optional operator filter forwarded to /api/facilities/search. */
  operator?: string;
  /** Called after each catalog page with the merged hits so far. */
  onPage?: (hitsSoFar: FacilitySearchHit[]) => void;
};

/** Server pageSize max for /api/facilities/search. */
export const FACILITY_SEARCH_PAGE_SIZE = 1000;
/** Smaller range page after a 1000-row timeout so 30-025 typeahead still returns. */
export const FACILITY_SEARCH_RETRY_PAGE_SIZE = 250;

const PAGE_FETCH_TIMEOUT_MS = 12_000;
/** Runaway cursor guard — larger than the NM+TX well catalog. */
const MAX_SEARCH_PAGES = 500;

export function formatApiPrefix(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
}

export function apiRangeMax(formatted: string): string {
  return `${formatted}${'9'.repeat(Math.max(0, 12 - formatted.length))}`;
}

function operatorQuery(operator?: string): string {
  const op = String(operator || '').trim();
  return op ? `&operator=${encodeURIComponent(op)}` : '';
}

function pushApiRangeUrls(urls: string[], digits: string, operator?: string): void {
  if (digits.length < 3) return;
  // Other-state prefixes (35-029 OK, etc.) are not in this catalog yet.
  if (queryIsOutOfScopeApiState(digits)) return;
  const formatted = formatApiPrefix(digits);
  const apiMax = apiRangeMax(formatted);
  const state = apiStateAbbrFromDigits(digits);
  const opQ = operatorQuery(operator);
  // 32-025 / 32-025-11 is Lea shorthand — hit 30-025* first, not an empty 32-* scan.
  const nmAlias = nmCountyPrefixFromShorthandDigits(digits);
  if (nmAlias) {
    const nmFormatted = formatApiPrefix(nmAlias);
    const nmMax = apiRangeMax(nmFormatted);
    urls.push(`/api/facilities/search?type=wells&apiMin=${encodeURIComponent(nmFormatted)}&apiMax=${encodeURIComponent(nmMax)}&state=NM${opQ}`);
    urls.push(`/api/facilities/search?type=all&api=${encodeURIComponent(nmFormatted)}${opQ}`);
  }
  if (state) {
    urls.push(`/api/facilities/search?type=wells&apiMin=${encodeURIComponent(formatted)}&apiMax=${encodeURIComponent(apiMax)}&state=${state}${opQ}`);
  } else if (!nmAlias) {
    urls.push(`/api/facilities/search?type=wells&apiMin=${encodeURIComponent(formatted)}&apiMax=${encodeURIComponent(apiMax)}&state=NM${opQ}`);
    urls.push(`/api/facilities/search?type=wells&apiMin=${encodeURIComponent(formatted)}&apiMax=${encodeURIComponent(apiMax)}&state=TX${opQ}`);
  }
}

export function mapFacilitySearchRow(raw: Record<string, unknown>): FacilitySearchHit | null {
  const apiNumber = String(raw.apiNumber || raw.api || raw.ocdId || raw.identifier || '').trim();
  const name = String(
    raw.name || raw.wellName || raw.facilityName || raw.well_name || apiNumber || 'Well',
  ).trim();
  const id = String(raw.id || raw.sourceId || raw.ocdId || apiNumber || name || '').trim();
  if (!id && !apiNumber && !name) return null;
  const lat = raw.latitude != null ? Number(raw.latitude) : NaN;
  const lng = raw.longitude != null ? Number(raw.longitude) : NaN;
  return {
    id: id || apiNumber || name,
    name: name || (apiNumber ? `Well ${apiNumber}` : 'Well'),
    apiNumber,
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lng) ? lng : null,
    county: raw.county != null ? String(raw.county) : null,
    state: raw.state != null ? String(raw.state) : null,
    operator:
      raw.operator != null
        ? String(raw.operator)
        : raw.operatorName != null
          ? String(raw.operatorName)
          : null,
    type: raw.type != null ? String(raw.type) : raw.facilityType != null ? String(raw.facilityType) : null,
    status: raw.status != null ? String(raw.status) : null,
    raw,
  };
}

function hitKey(hit: FacilitySearchHit): string {
  return `${hit.apiNumber}|${hit.name}|${hit.latitude}|${hit.longitude}|${hit.id}`;
}

function withPageParams(url: string, cursor: number, pageSize: number): string {
  const parsed = new URL(url, 'https://ziawellnav.local');
  parsed.searchParams.delete('limit');
  parsed.searchParams.set('pageSize', String(pageSize));
  parsed.searchParams.set('cursor', String(cursor));
  return `${parsed.pathname}${parsed.search}`;
}

type SearchPage = {
  hits: FacilitySearchHit[];
  hasMore: boolean;
  nextCursor: number | null;
  failed?: boolean;
};

async function fetchSearchPage(url: string, headers?: HeadersInit): Promise<SearchPage> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), PAGE_FETCH_TIMEOUT_MS) : null;
  try {
    const res = await fetch(url, {
      ...(headers ? { headers } : {}),
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    if (!res.ok) return { hits: [], hasMore: true, nextCursor: null, failed: true };
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return { hits: [], hasMore: true, nextCursor: null, failed: true };
    }
    const data = (await res.json()) as {
      facilities?: Array<Record<string, unknown>>;
      wells?: Array<Record<string, unknown>>;
      results?: Array<Record<string, unknown>>;
      hasMore?: boolean;
      nextCursor?: string | number | null;
    };
    const rows = data.facilities || data.wells || data.results || [];
    const hits: FacilitySearchHit[] = [];
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const hit = mapFacilitySearchRow(row as Record<string, unknown>);
      if (hit) hits.push(hit);
    }
    const nextRaw = data.nextCursor;
    const nextCursor = nextRaw == null || nextRaw === '' ? null : Number(nextRaw);
    return {
      hits,
      hasMore: Boolean(data.hasMore),
      nextCursor: Number.isFinite(nextCursor) ? nextCursor : null,
    };
  } catch {
    return { hits: [], hasMore: true, nextCursor: null, failed: true };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function fetchAllPages(
  url: string,
  headers: HeadersInit | undefined,
  merged: FacilitySearchHit[],
  seen: Set<string>,
  cap: number | undefined,
  onPage: ((hitsSoFar: FacilitySearchHit[]) => void) | undefined,
  preferredPageSize: number,
): Promise<void> {
  let cursor = 0;
  let pageSize = preferredPageSize;
  let didRetry = false;
  for (let page = 0; page < MAX_SEARCH_PAGES; page += 1) {
    if (cap != null && merged.length >= cap) return;
    const pageUrl = withPageParams(url, cursor, pageSize);
    let result = await fetchSearchPage(pageUrl, headers);
    if (result.failed && !didRetry) {
      didRetry = true;
      const retrySize = cap != null
        ? Math.min(FACILITY_SEARCH_RETRY_PAGE_SIZE, Math.max(cap, 1))
        : FACILITY_SEARCH_RETRY_PAGE_SIZE;
      pageSize = retrySize;
      result = await fetchSearchPage(withPageParams(url, cursor, pageSize), headers);
    }
    if (result.failed) return;
    let added = 0;
    for (const hit of result.hits) {
      const key = hitKey(hit);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(hit);
      added += 1;
      if (cap != null && merged.length >= cap) break;
    }
    if (added > 0) onPage?.(merged);
    if (cap != null && merged.length >= cap) return;
    // Name-search payloads used to ignore cursor and repeat page 1 with hasMore.
    // Zero new rows means this catalog is exhausted — do not spin 500 pages.
    if (added === 0 || !result.hasMore || result.hits.length === 0) return;
    const next = result.nextCursor != null ? result.nextCursor : cursor + result.hits.length;
    if (!Number.isFinite(next) || next <= cursor) return;
    cursor = next;
  }
}

function buildSearchUrls(
  q: string,
  preferWells: boolean,
  looksLikeApi: boolean,
  operator?: string,
): { primary: string[]; fallbacks: string[] } {
  const digits = q.replace(/\D/g, '');
  const opQ = operatorQuery(operator);
  const primary: string[] = [];
  const fallbacks: string[] = [];

  // Preserve the OCD prefix: its digits are not a well API state/county.
  if (isNmOcdFacilityId(q)) {
    primary.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=facilities&state=NM${opQ}`);
    fallbacks.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=all&state=NM${opQ}`);
    return { primary, fallbacks };
  }

  if (looksLikeApi) {
    // Prefix / county-range first so NM 30-025-* is fetched before a page of
    // TX last-5 collisions. Never use query= name for digit-only searches.
    // Other-state official prefixes (35 OK, 05 CO, …) stay empty until we add them.
    if (queryIsOutOfScopeApiState(digits)) {
      return { primary, fallbacks };
    }
    if (digits.length >= 3) {
      pushApiRangeUrls(primary, digits, operator);
      // 5-digit county prefixes (30-025) must not use api= — the server treats
      // short api= as invalid and falls through to a name/index dump.
      if (digits.length >= 10) {
        const formatted = formatApiPrefix(digits);
        const state = apiStateAbbrFromDigits(digits);
        const stateQ = state ? `&state=${state}` : '';
        const apiToken = encodeURIComponent(formatted);
        fallbacks.push(`/api/facilities/search?type=all&api=${apiToken}${stateQ}${opQ}`);
        fallbacks.push(`/api/facilities/search?type=wells&api=${apiToken}${stateQ}${opQ}`);
        fallbacks.push(`/api/facilities/search?type=facilities&api=${apiToken}${stateQ}${opQ}`);
      }
    }
    return { primary, fallbacks };
  }

  if (preferWells) {
    primary.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=wells${opQ}`);
  } else {
    // General map search includes facilities even when wells share the name.
    primary.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=all${opQ}`);
    primary.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=facilities${opQ}`);
  }
  // Always query NM and TX well catalogs so service wizards find both basins.
  primary.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=wells&state=NM${opQ}`);
  primary.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=wells&state=TX${opQ}`);
  if (preferWells) {
    fallbacks.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=all${opQ}`);
    fallbacks.push(`/api/facilities/search?query=${encodeURIComponent(q)}&type=facilities${opQ}`);
  }
  if (digits.length >= 3) {
    fallbacks.push(`/api/facilities/search?type=all&api=${encodeURIComponent(digits)}${opQ}`);
    fallbacks.push(`/api/facilities/search?type=wells&api=${encodeURIComponent(digits)}${opQ}`);
  }
  return { primary, fallbacks };
}

/**
 * Search facilities by API number or well/lease name.
 * Always re-ranks multi-word queries so "angel eyes" surfaces ANGEL EYES.
 * Pages every catalog URL until hasMore is false unless `limit` is passed.
 */
export async function searchFacilitiesByQuery(
  query: string,
  options: SearchFacilitiesByQueryOptions = {},
): Promise<FacilitySearchHit[]> {
  const q = String(query || '').trim();
  if (q.length < 2) return [];

  const cap = options.limit != null && Number.isFinite(options.limit) && options.limit > 0
    ? Math.max(1, Math.floor(options.limit))
    : undefined;
  const preferWells = options.preferWells !== false;
  const looksLikeApi = looksLikeApiSearchQuery(q);
  const { primary, fallbacks } = buildSearchUrls(q, preferWells, looksLikeApi, options.operator);
  const preferredPageSize = cap != null
    ? Math.min(FACILITY_SEARCH_PAGE_SIZE, cap)
    : FACILITY_SEARCH_PAGE_SIZE;

  const merged: FacilitySearchHit[] = [];
  const seen = new Set<string>();

  const onPage = options.onPage && isNmOcdFacilityId(q)
    ? (hits: FacilitySearchHit[]) => options.onPage?.(hits.filter(h => facilityNameMatchesQuery(h.name, h.apiNumber, q)))
    : options.onPage;

  const runUrls = async (urls: string[]) => {
    for (const url of urls) {
      if (cap != null && merged.length >= cap) return;
      await fetchAllPages(url, options.headers, merged, seen, cap, onPage, preferredPageSize);
    }
  };

  await runUrls(primary);
  // Fallbacks only when the primary catalogs returned nothing — extra type
  // scans must not replace a full prefix page with a short typeahead dump.
  if (merged.length === 0) {
    await runUrls(fallbacks);
  } else if (!looksLikeApi && !isNmOcdFacilityId(q) && !preferWells) {
    await runUrls(fallbacks);
  }

  const matched = (looksLikeApi || isNmOcdFacilityId(q))
    ? merged.filter((h) => facilityNameMatchesQuery(h.name, h.apiNumber, q))
    : merged;

  const ranked = rankFacilitySearchHits(
    matched,
    q,
    (h) => h.name,
    (h) => h.apiNumber,
  );
  return cap != null ? ranked.slice(0, cap) : ranked;
}
